/**
 * Design System - QuickCard Component
 * Compact card component for quick previews with image
 */

import React from 'react';
import Image from 'next/image';
import {
  typographyColors,
  backgroundColors,
  spacing,
  borders,
  typography,
  fontPrimary,
  dimensions,
  shadows,
} from '../../utils';

export interface QuickCardProps {
  title: string;
  image: string;
  subtitle?: string;
  onClick?: () => void;
  imageAspectRatio?: 'square' | 'video' | 'auto';
}

export const QuickCard: React.FC<QuickCardProps> = ({
  title,
  image,
  subtitle,
  onClick,
  imageAspectRatio = 'auto',
}) => {
  const aspectRatioStyles = {
    square: { aspectRatio: '1/1' },
    video: { aspectRatio: '16/9' },
    auto: {},
  };

  return (
    <div
      onClick={onClick}
      style={{
        width: dimensions.component.cardQuick.width,
        height: dimensions.component.cardQuick.height,
        backgroundColor: backgroundColors.primary,
        borderRadius: borders.radius.lg,
        overflow: 'hidden',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.2s',
        display: 'flex',
        flexDirection: 'column',
      }}
      onMouseEnter={e => {
        if (onClick) {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = shadows.md;
        }
      }}
      onMouseLeave={e => {
        if (onClick) {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = 'none';
        }
      }}
    >
      <div
        style={{
          width: '100%',
          height: dimensions.component.activityCardImage.height,
          position: 'relative',
          overflow: 'hidden',
          borderRadius: borders.radius.lg,
          transform: 'translateZ(0)', // Force GPU acceleration for smoother rendering
          willChange: 'transform',
          isolation: 'isolate', // Create new stacking context for better clipping
          ...aspectRatioStyles[imageAspectRatio],
        }}
      >
        <Image
          src={image}
          alt={title}
          fill
          style={{
            objectFit: 'cover',
            borderRadius: borders.radius.lg,
            transform: 'translateZ(0)', // Force GPU acceleration
            WebkitBackfaceVisibility: 'hidden',
            backfaceVisibility: 'hidden',
          }}
          onError={e => {
            // Fallback to default image if the provided image fails to load
            const target = e.target as HTMLImageElement;
            if (
              target.src !==
              'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=300&fit=crop&q=80'
            ) {
              target.src =
                'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=300&fit=crop&q=80';
            }
          }}
        />
      </div>
      <div
        style={{
          padding: spacing[2],
          display: 'flex',
          flexDirection: 'column',
          gap: spacing[0.5],
          flex: 1,
        }}
      >
        <span
          style={{
            fontFamily: fontPrimary,
            fontSize: typography.size.sm,
            fontWeight: typography.weight.semibold,
            color: typographyColors.primary,
            lineHeight: 'normal',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {title}
        </span>
        {subtitle && (
          <span
            style={{
              fontFamily: fontPrimary,
              fontSize: typography.size.xs,
              fontWeight: typography.weight.regular,
              color: typographyColors.tertiary,
              lineHeight: 'normal',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {subtitle}
          </span>
        )}
      </div>
    </div>
  );
};
